import axios, { AxiosResponse } from 'axios';
import { uploadListFile } from 'Services';
import { MutationOptions, useMutation } from '@tanstack/react-query';
import { callAll } from 'Utils';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { FILE_UPLOAD_STATUS } from 'Common/enums';

type HookArgs = {
    setState: Dispatch<SetStateAction<ImporterStateProps>>;
    mutationOptions?: Omit<MutationOptions<AxiosResponse, unknown, unknown, unknown>, 'mutationKey'>;
};

type HookVars = { isInitiateUpload: boolean; formData?: FormData; abortController?: AbortController };

const useUploadListFile = ({ setState, mutationOptions }: HookArgs) => {
    const { t } = useTranslation();

    const defaultSuccessHandler = (result: AxiosResponse, variables: HookVars) => {
        const { isInitiateUpload } = variables;
        if (isInitiateUpload) {
            const receivedUploadId: string = result.data;
            setState((prevState) => ({
                ...prevState,
                uploadId: receivedUploadId,
                uploadProgress: 2,
                fileUploadStatus: FILE_UPLOAD_STATUS.UPLOADING
            }));
        } else {
            setState((prevState) => ({
                ...prevState,
                partCompleted: prevState.partCompleted + 1
            }));
        }
    };

    return useMutation(
        ({ formData, abortController, isInitiateUpload }) =>
            uploadListFile(formData, abortController, isInitiateUpload),
        {
            onSuccess: callAll(defaultSuccessHandler, mutationOptions?.onSuccess),
            onError: async (error, variables, context) => {
                if (!axios.isCancel(error)) {
                    setState((prevState) => ({
                        ...prevState,
                        errorText: t('File failed to upload due to a network error')
                    }));
                    mutationOptions?.onError?.(error, variables, context);
                }
            }
        }
    );
};

export default useUploadListFile;
